/**
 * Figure — a lazy-loaded image with optional caption + credit, and click-to-
 * expand into a full-size lightbox (Dialog). Used by About (diagrams + photos),
 * Founder, and anywhere a marketing block needs imagery. Composes the Dialog
 * primitive; tokens + Tailwind only.
 */
import type { ReactNode } from 'react';
import { Expand } from 'lucide-react';
import { Dialog, DialogContent, DialogTrigger, DialogTitle } from '../dialog';
import { cn } from '../../utils';

export interface FigureProps {
  readonly src: string;
  readonly alt: string;
  readonly caption?: ReactNode;
  readonly credit?: ReactNode;
  /** Constrain to content width (default) or let it run full-bleed within the article. */
  readonly wide?: boolean;
  /** Drop the default vertical margin (when composed inside Split / FigureGrid). */
  readonly bare?: boolean;
  /** Object-fit cover at a fixed aspect (gallery tiles); default is natural height. */
  readonly cover?: boolean;
  /** Click to open a full-size lightbox (default true). */
  readonly expandable?: boolean;
  /** Float the figure so adjacent prose wraps around it (magazine layout). Full
   *  width on mobile, ~40% floated from sm up. */
  readonly float?: 'left' | 'right';
  /** Intrinsic pixel dimensions (codegen-stamped for raster images) so the
   *  browser reserves the layout box — no CLS, satisfies the explicit-dimensions
   *  audit. Display size stays CSS-driven (w-full / aspect classes). */
  readonly width?: number;
  readonly height?: number;
  readonly className?: string;
}

export function Figure({
  src, alt, caption, credit, wide = false, bare = false, cover = false, expandable = true, float, width, height, className,
}: FigureProps) {
  const dims = width && height ? { width, height } : {};
  const floatCls = float
    ? cn('mb-(--space-4) mt-(--space-1) w-full sm:w-2/5', float === 'left' ? 'sm:float-left sm:mr-(--space-6)' : 'sm:float-right sm:ml-(--space-6)')
    : null;
  const imgClass = cn(
    'w-full rounded-card border border-border bg-muted',
    cover && 'aspect-4/3 object-cover',
  );

  // Codegen diagrams emit a light + a dark SVG (<id>.svg / <id>-dark.svg);
  // swap by theme. Photos/other images have no dark variant — render as-is.
  const darkSrc = /^\/diagrams\/[^?]+\.svg$/.test(src) && !src.includes('-dark')
    ? src.replace(/\.svg$/, '-dark.svg')
    : null;

  const renderImg = (extra: string) =>
    darkSrc ? (
      <>
        <img src={src} alt={alt} loading="lazy" decoding="async" {...dims} className={cn(imgClass, 'dark:hidden', extra)} />
        <img src={darkSrc} alt={alt} loading="lazy" decoding="async" {...dims} className={cn(imgClass, 'hidden dark:block', extra)} />
      </>
    ) : (
      <img src={src} alt={alt} loading="lazy" decoding="async" {...dims} className={cn(imgClass, extra)} />
    );

  return (
    <figure className={cn(floatCls ?? cn(bare ? '' : '', wide ? 'mx-[calc(50%-50vw)] max-w-[100vw]' : 'mx-auto'), className)}>
      {expandable ? (
        <Dialog>
          <DialogTrigger asChild>
            <button type="button" className="group relative block w-full cursor-zoom-in" aria-label={`Expand image: ${alt}`}>
              {renderImg('')}
              <span className="pointer-events-none absolute right-2 top-2 inline-flex size-8 items-center justify-center rounded-md bg-background/70 text-foreground opacity-0 backdrop-blur transition-opacity group-hover:opacity-100">
                <Expand className="size-4" aria-hidden="true" />
              </span>
            </button>
          </DialogTrigger>
          <DialogContent size="full" className="bg-card p-3">
            <DialogTitle className="sr-only">{alt}</DialogTitle>
            {renderImg('max-h-[82vh] object-contain')}
            {(caption || credit) && (
              <p className="mt-3 text-center text-sm text-muted-foreground">
                {caption}
                {credit && <span className="block text-xs opacity-70">{credit}</span>}
              </p>
            )}
          </DialogContent>
        </Dialog>
      ) : (
        renderImg('')
      )}
      {(caption || credit) && (
        <figcaption className="mt-3 text-sm text-muted-foreground">
          {caption}
          {credit && <span className="block text-xs opacity-70">{credit}</span>}
        </figcaption>
      )}
    </figure>
  );
}
